import PropTypes from "prop-types";
import React, { Fragment, useState, useEffect } from "react";
import {
  useAsyncDebounce,
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { Button, Col, Input, Row, Spinner, Table } from "reactstrap";
import { DefaultColumnFilter, Filter } from "./filters";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from "../../constants/tables";

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, title, ...rest }, ref) => {
    const defaultRef = React.useRef()
    const resolvedRef = ref || defaultRef

    React.useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = indeterminate
      }
    }, [resolvedRef, indeterminate])

    return (
      <div className="text-center" style={{ display: 'flex', justifyContent: 'center' }}>
        <input type="checkbox" ref={resolvedRef} title={title} {...rest} style={{ cursor: 'pointer', transform: 'scale(1.2)' }} />
      </div>
    )
  }
)

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isAddOrder,
  isAddTableWithoutBorderStrap,
  handleOrderClicks,
  isAddCustomer,
  isAddUsers,
  handleUserClicks,
  handleCustomerClicks,
  isAddTableBorderStrap,
  isMenu,
  isAddInvoice,
  handleInvoiceClicks,
  loading,
  total,
  currentPage,
  getPagination,
  renderRowSubComponent,
  onPageSizeChange,
  isRowSelect,
  onSelectedRowsChange,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    visibleColumns,
    selectedFlatRows,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: currentPage,
        pageSize: DEFAULT_PAGE_SIZE,
      },
      manualPagination: true,
      pageCount: total,
      autoResetSelectedRows: false,
      getRowId: (row, relativeIndex) => row.id !== undefined ? String(row.id) : String(relativeIndex),
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (isRowSelect) {
        hooks.visibleColumns.push((columns) => [
          {
            id: "selection",
            width: "3%",
            minWidth: 40,
            Header: ({ getToggleAllPageRowsSelectedProps }) => (
              <IndeterminateCheckbox
                {...getToggleAllPageRowsSelectedProps()}
                title="Seleccionar todas las muestras de esta página"
              />
            ),
            Cell: ({ row }) => (
              <IndeterminateCheckbox
                {...row.getToggleRowSelectedProps()}
                title="Seleccionar muestra"
              />
            ),
          },
          ...columns,
        ]);
      }
    }
  );

  useEffect(() => {
    if (onSelectedRowsChange) {
      onSelectedRowsChange(selectedFlatRows ? selectedFlatRows.map(d => d.original) : []);
    }
  }, [selectedFlatRows, onSelectedRowsChange]);



  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "";
  };

  const onChangeInSelect = (event) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    getPagination(`?limit=${newSize}&page=${DEFAULT_PAGE}`);

    if (typeof onPageSizeChange === 'function') {
      onPageSizeChange(newSize); // Notify parent
    }
  };

  const onChangeInInput = (event) => {
    const page = event.target.value ? Number(event.target.value) - 1 : DEFAULT_PAGE;
    gotoPage(page);
    getPagination(`?limit=${pageSize}&page=${page}`)
  };

  const onPreviousNextPage = (isNext) => {
    if (isNext) {
      getPagination(`?limit=${pageSize}&page=${pageIndex + 1}`)
      nextPage()
    } else {
      getPagination(`?limit=${pageSize}&page=${pageIndex - 1}`)
      previousPage()
    }
  }

  return (
    <Fragment>
      <Row className="mb-3">
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
        {isAddUsers && (
          <Col sm={7}>
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="waves-effect waves-light mb-3 btn btn-success"
                onClick={handleUserClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New
              </Button>
            </div>
          </Col>
        )}
        {isAddInvoice && (
          <Col sm={7}>
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="waves-effect waves-light mb-3 btn btn-success"
                onClick={handleInvoiceClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add Invoice
              </Button>
            </div>
          </Col>
        )}
        {isAddCustomer && (
          <Col sm={7}>
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="waves-effect waves-light mb-3 btn btn-success"
                onClick={handleCustomerClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Customer
              </Button>
            </div>
          </Col>
        )}
        {isAddOrder && (
          <Col sm={7}>
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="waves-effect waves-light mb-3 btn btn-success"
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}

      </Row>

      {isAddTableWithoutBorderStrap && (
        <div className="table table-bordered ">
          <Table bordered hover {...getTableProps()} className="react_table">
            <thead className="thead-dark">
              {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th key={column.id}>
                      <div className="mb-2" {...(column.id === 'selection' ? {} : column.getSortByToggleProps())}>
                        {column.render("Header")}
                        {column.id !== 'selection' && generateSortingIndicator(column)}
                      </div>
                      <Filter column={column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr {...row.getRowProps({

                    })}>
                      {row.cells.map((cell) => {
                        return (
                          <td key={cell.id} {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {isAddTableBorderStrap && (
        <div className="table">
          {
            loading ?
              <div className="text-center py-5">
                <Spinner
                  color="primary"
                >
                </Spinner>
                <div className="row-center mt-2">
                  {' '}Cargando...
                </div>
              </div> :
              <Table bordered hover responsive
                className="table-responsive"
                {...getTableProps()}
              >
                <thead>
                  {headerGroups?.map((headerGroup) => (
                    <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup?.headers?.map((column) => (
                        <th key={column.id} style={{}} {...column.getHeaderProps({
                          style: { minWidth: column.minWidth, width: column.width, backgroundColor: '#EDEDED', textAlign: 'center' },
                        })} >
                          <div className="mb-2" {...(column.id === 'selection' ? {} : column.getSortByToggleProps())}>
                            {column.render("Header")}
                            {column.id !== 'selection' && generateSortingIndicator(column)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                {
                  (data.length === 0 || data === '') ?
                    <tbody>
                      <tr className="text-center">
                        <td colSpan={columns.length + 100}>
                          No hay elementos encontrados <br />
                          <i className="mdi mdi-36px mdi-sim-alert me-1" />
                        </td>
                      </tr>
                    </tbody> :
                    <tbody {...getTableBodyProps()}>
                      {page?.map((row) => {
                        prepareRow(row);
                        return (
                          <Fragment key={row.getRowProps().key}>
                            <tr {...row.getRowProps({

                            })}>
                              {row?.cells?.map((cell) => {
                                return (
                                  <td key={cell.id} {...cell.getCellProps({
                                    style: {
                                      minWidth: cell.column.minWidth,
                                      width: cell.column.width, textAlign: 'center',
                                      borderTopColor: '#B9B9B9', borderTopWidth: 3,
                                    },
                                  })}>
                                    {cell.render("Cell")}
                                  </td>
                                );
                              })}
                            </tr>
                            {row.isExpanded ? (
                              <tr>
                                <td colSpan={visibleColumns.length} >
                                  {renderRowSubComponent({ row })}
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        );
                      })}
                    </tbody>

                }
              </Table>
          }
        </div>
      )}

      {isAddTableBorderStrap && isMenu && (
        <div className="table">
          <Table bordered hover
            className="table-responsive"
            {...getTableProps()}
          >
            <thead>
              {headerGroups?.map((headerGroup) => (
                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup?.headers?.map((column) => (
                    <th key={column.id} style={{}} {...column.getHeaderProps({
                      style: { minWidth: column.minWidth, width: column.width, backgroundColor: '#EDEDED', textAlign: 'center' },
                    })} >
                      <div className="mb-2" {...(column.id === 'selection' ? {} : column.getSortByToggleProps())}>
                        {column.render("Header")}
                        {column.id !== 'selection' && generateSortingIndicator(column)}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            {
              (data.length === 0 || data === '') ?
                <tbody>
                  <tr className="text-center">
                    <td colSpan={columns.length + 100}>
                      No hay elementos encontrados <br />
                      <i className="mdi mdi-36px mdi-sim-alert me-1" />
                    </td>
                  </tr>
                </tbody> :
                <tbody {...getTableBodyProps()}>
                  {page?.map((row) => {
                    prepareRow(row);
                    return (
                      <Fragment key={row.getRowProps().key}>
                        <tr {...row.getRowProps({

                        })}>
                          {
                            row?.cells?.map((cell) => {
                              return (
                                <td key={cell.id} {...cell.getCellProps({
                                  style: {
                                    minWidth: cell.column.minWidth,
                                    width: cell.column.width, textAlign: 'center'
                                  },
                                })} >
                                  {cell.render("Cell")}
                                </td>
                              );
                            })
                          }
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
            }
          </Table>
        </div>
      )}

      {
        pageCount > 0 ?
          <Row className="justify-content-md-end justify-content-center align-items-center">
            <Col className="col-md-auto">
              <select
                className="form-select w-100"
                value={pageSize}
                onChange={onChangeInSelect}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}/Página
                  </option>
                ))}
              </select>
            </Col>
            <Col className="col-md-auto">
              <div className="d-flex gap-1">
                <Button
                  outline
                  onClick={() => { onPreviousNextPage(false) }}
                  disabled={!canPreviousPage}
                >
                  {"<"}
                </Button>
              </div>
            </Col>
            <Col className="col-md-auto d-none d-md-block">
              <strong>
                {pageIndex + 1} de {pageCount}
              </strong>
            </Col>
            <Col className="col-md-auto">
              <Input
                type="number"
                min={1}
                style={{ width: 70 }}
                max={pageCount}
                value={pageIndex + 1}
                onChange={onChangeInInput}
              />
            </Col>

            <Col className="col-md-auto">
              <div className="d-flex gap-1">
                <Button outline onClick={() => { onPreviousNextPage(true) }} disabled={!canNextPage}>
                  {">"}
                </Button>
              </div>
            </Col>
          </Row> : <div className="mb-5"></div>
      }
    </Fragment>
  );
};

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <React.Fragment>
      <Col sm={12}>
        <div className="search-box me-2 mb-2 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Search this table
              </span>
              <input
                onChange={(e) => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`${count} records...`}
                value={value || ""}
              />
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </Col>
    </React.Fragment>
  );
}

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
  onPageSizeChange: PropTypes.func,
};

export default TableContainer;
